import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight, Check, X, Users, Network, FileText,
  Gavel, Trophy, Handshake, ShieldCheck, Sparkles, Building2,
  Target, Radio,
} from "lucide-react";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import { usePageMeta } from "@/hooks/usePageMeta";

import shipperImg from "@/assets/shipper-professional.jpg";
import brokerImg from "@/assets/broker-professional.jpg";
import StepIllustration from "@/components/marketing/mockups/StepIllustration";

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-bold tracking-wider ${className}`}>
    {children}
  </div>
);

const CheckRow: React.FC<{ children: React.ReactNode; dark?: boolean }> = ({ children, dark }) => (
  <li className="flex items-start gap-2.5">
    <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${dark ? "bg-white/15" : "bg-[#02857E]/10"}`}>
      <Check className={`h-3 w-3 ${dark ? "text-white" : "text-[#02857E]"}`} />
    </span>
    <span className={`text-sm leading-relaxed ${dark ? "text-gray-200" : "text-gray-700"}`}>{children}</span>
  </li>
);

const XRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100">
      <X className="h-3 w-3 text-gray-400" />
    </span>
    <span className="text-sm leading-relaxed text-gray-500">{children}</span>
  </li>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const LRDirectAccessPage: React.FC = () => {
  const { user, profile, loading } = useAuth();

  usePageMeta({
    fullTitle: true,
    title: "LR Direct — Direct Shipper Relationships for Brokers | LaneRunner",
    description:
      "LR Direct connects shippers and brokers in private networks and a curated open marketplace. Free for shippers. Direct relationships. No middleman margin.",
  });

  // Logged-in shortcut: send authenticated users into the app.
  if (!loading && user && profile) {
    return <Navigate to="/lr-direct" replace />;
  }


  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />

      {/* ============================================================ */}
      {/* 1. HERO                                                        */}
      {/* ============================================================ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-white to-[#F7FBFB] pt-12 sm:pt-20 pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: copy + CTAs */}
          <div>
            <Eyebrow>
              <Radio className="h-3.5 w-3.5" /> DIRECT FREIGHT MARKETPLACE
            </Eyebrow>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-gray-900">
              Direct shipper relationships.{" "}
              <span className="text-[#02857E]">No middlemen taking margin.</span>
            </h1>
            <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-xl">
              LR Direct connects shippers and brokers in private networks and a curated open
              marketplace. Shippers post freight. Brokers bid. Direct relationships. Real
              freight. No noise.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-[#02857E] text-white hover:bg-[#026d67] px-7 py-6 text-base font-semibold shadow-md"
              >
                <Link to="/auth?mode=signup&role=shipper">
                  Get Started Free as a Shipper <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-2 border-[#02857E] text-[#02857E] hover:bg-[#02857E]/5 px-7 py-6 text-base font-semibold"
              >
                <Link to="/pricing?role=broker">
                  View Broker Plans
                </Link>
              </Button>
            </div>
            <p className="mt-5 text-xs text-gray-500">
              Free for shippers, always. Brokers from $99.99/mo.
            </p>
          </div>

          {/* Right: embedded demo video (16:9) */}
          <div>
            <p className="mb-3 text-sm font-semibold text-[#02857E]">
              Watch how LR Direct works
            </p>
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10 bg-black">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/YTvMHhhbpWs?rel=0&modestbranding=1"
                title="LR Direct demo video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 2. WHAT IS LR DIRECT — shipper vs broker                       */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>WHAT IS LR DIRECT</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-gray-900">
              Two sides. One direct relationship.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* FOR SHIPPERS */}
            <div className="rounded-2xl border border-gray-200 p-8 bg-white hover:shadow-lg transition">
              <p className="text-xs font-bold tracking-wider text-[#02857E]">FOR SHIPPERS</p>
              <h3 className="mt-3 text-2xl font-bold text-gray-900">
                Build your private broker network
              </h3>
              <p className="mt-3 text-gray-600 leading-relaxed">
                Invite brokers you trust. Post freight directly to them. Or post to the open
                LR Direct marketplace to find new broker relationships. All free, always.
              </p>
              <ul className="mt-6 space-y-3">
                <CheckRow>Create private networks</CheckRow>
                <CheckRow>Invite vetted brokers</CheckRow>
                <CheckRow>Direct relationships</CheckRow>
                <CheckRow>No margin taken on freight</CheckRow>
                <CheckRow>Free for shippers</CheckRow>
              </ul>
            </div>

            {/* FOR BROKERS */}
            <div className="rounded-2xl border border-gray-200 p-8 bg-white hover:shadow-lg transition">
              <p className="text-xs font-bold tracking-wider text-[#02857E]">FOR BROKERS</p>
              <h3 className="mt-3 text-2xl font-bold text-gray-900">
                Direct shipper freight, fair competition
              </h3>
              <p className="mt-3 text-gray-600 leading-relaxed">
                Get invited to private shipper networks. Or upgrade to LR Direct Full and bid
                on the entire open LR Direct marketplace. Win freight, win the shipper
                relationship.
              </p>
              <ul className="mt-6 space-y-3">
                <CheckRow>Bid on shipper-invited private network loads</CheckRow>
                <CheckRow>Premium tier ($99.99/mo) includes private bidding</CheckRow>
                <CheckRow>LR Direct Full ($249.99/mo) opens entire marketplace</CheckRow>
                <CheckRow>Direct relationships from day one</CheckRow>
                <CheckRow>No margin taken on your freight</CheckRow>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 3. HOW LR DIRECT WORKS                                         */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-24 bg-[#F7FBFB]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>HOW IT WORKS</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-gray-900">
              Six steps from sign-up to direct relationship
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                n: 1,
                title: "Shipper Joins",
                step: "shipperJoins" as const,
                icon: Users,
                body:
                  "Shippers sign up free. Our team verifies the operation. Once approved, they can post freight to private networks or the open marketplace.",
              },
              {
                n: 2,
                title: "Shipper Builds Network",
                step: "buildNetwork" as const,
                icon: Network,
                body:
                  "Shippers can create private broker networks by sharing their LaneRunner ID with trusted brokers, or by inviting brokers directly via email.",
              },
              {
                n: 3,
                title: "Shipper Posts Freight",
                step: "postFreight" as const,
                icon: FileText,
                body:
                  "Shipper posts a load with lane, equipment, and bid window. They choose: private network only, open marketplace, or both.",
              },
              {
                n: 4,
                title: "Brokers Compete",
                step: "brokersCompete" as const,
                icon: Gavel,
                body:
                  "Eligible brokers see the load and submit bids. Private network brokers see invited freight only (with Premium). LR Direct Full brokers see everything.",
              },
              {
                n: 5,
                title: "Shipper Awards the Bid",
                step: "awardBid" as const,
                icon: Trophy,
                body:
                  "Best bid wins. The shipper awards the load to their preferred broker. Direct relationship begins.",
              },
              {
                n: 6,
                title: "Relationship Is Yours",
                step: "relationship" as const,
                icon: Handshake,
                body:
                  "Once awarded, the broker handles the load directly with the shipper. LaneRunner takes no margin. We just opened the door — the relationship is theirs.",
              },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="rounded-2xl bg-white border border-gray-200 overflow-hidden hover:shadow-lg transition">
                  <div className="relative aspect-[16/9]">
                    <StepIllustration step={s.step} className="absolute inset-0" />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 text-[#02857E] text-xs font-bold shadow-sm">
                      <Icon className="h-3.5 w-3.5" /> Step {s.n}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900">{s.title}</h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 4. FOR SHIPPERS (DETAILED)                                     */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 ring-1 ring-black/5">
              <img src={shipperImg} alt="Shipper using LaneRunner" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <Eyebrow>FOR SHIPPERS</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-gray-900">
              Shippers — Move Freight Smarter
            </h2>
            <p className="mt-5 text-gray-600 leading-relaxed">
              Tight capacity? Urgent freight? LR Direct gives shippers the tools to organize
              broker relationships, post freight to specific networks, and find new vetted
              brokers in the open marketplace.
            </p>
            <p className="mt-3 text-gray-900 font-semibold">All free. Always free for shippers.</p>

            <div className="mt-8 space-y-5">
              {[
                {
                  icon: Target,
                  title: "Private Network Control",
                  body:
                    "Create multiple named networks (e.g., 'California Brokers', 'Refrigerated Specialists'). Invite brokers you trust. Send freight directly to them with bid windows you control.",
                },
                {
                  icon: Building2,
                  title: "Open Marketplace Reach",
                  body:
                    "Need broader coverage? Post to the LR Direct open marketplace and reach vetted brokers across the platform. Compare bids, award the best fit.",
                },
                {
                  icon: Handshake,
                  title: "Direct Relationships",
                  body:
                    "Once you award a load, the broker is yours. LaneRunner doesn't take a percentage of your freight or get in the middle of your business relationships.",
                },
              ].map((f) => {
                const I = f.icon;
                return (
                  <div key={f.title} className="flex gap-4">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#02857E]/10 text-[#02857E]">
                      <I className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">{f.title}</h3>
                      <p className="mt-1 text-sm text-gray-600 leading-relaxed">{f.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-[#02857E] text-white hover:bg-[#026d67] px-7 py-6 text-base font-semibold"
              >
                <Link to="/auth?mode=signup&role=shipper">Sign Up Free <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <p className="mt-3 text-xs text-gray-500">
                No credit card required. Manual verification within 24 hours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 5. FOR BROKERS (DETAILED)                                      */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-24 bg-[#F7FBFB]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-14">
            <div>
              <Eyebrow>FOR BROKERS</Eyebrow>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-gray-900">
                Brokers — Build Your Book of Business
              </h2>
              <p className="mt-5 text-gray-600 leading-relaxed">
                LR Direct is invitation-driven and curated. Get invited to private shipper
                networks. Or compete in the open marketplace. Either way, every relationship
                is direct shipper-to-broker. No middleman taking margin.
              </p>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 ring-1 ring-black/5">
              <img src={brokerImg} alt="Broker closing a deal" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>

          {/* Single broker CTA */}
          <div className="max-w-2xl mx-auto">
            {/* Broker Path */}
            <div className="relative rounded-2xl bg-gradient-to-br from-gray-900 via-gray-900 to-[#02857E] text-white p-8 shadow-xl ring-1 ring-white/10">
              <p className="text-xs font-bold tracking-wider text-[#7DE2DC]">FOR BROKERS</p>
              <h3 className="mt-3 text-2xl font-bold">Premium or Full Access</h3>
              <p className="mt-3 text-sm text-white/80 leading-relaxed">
                Premium unlocks private network bidding for the shippers who invite you. LR Direct Full opens the entire open marketplace. Subscribe monthly or yearly.
              </p>
              <ul className="mt-6 space-y-3">
                <CheckRow dark>Premium: bid on shipper-invited private networks</CheckRow>
                <CheckRow dark>LR Direct Full: bid on entire open marketplace</CheckRow>
                <CheckRow dark>ATLAS AI suite, ad-free, boost credits</CheckRow>
                <CheckRow dark>Cancel anytime, monthly or yearly billing</CheckRow>
              </ul>
              <Button
                asChild
                className="mt-7 w-full rounded-full bg-white text-[#02857E] hover:bg-white/90 font-semibold"
              >
                <Link to="/pricing?role=broker">View Broker Plans <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <p className="mt-3 text-xs text-white/70 text-center">
                Compare tiers and choose your plan on the pricing page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 6. WHY LR DIRECT                                               */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>WHY LR DIRECT</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-gray-900">
              Built on direct relationships, not margins
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Handshake,
                title: "Direct Relationships",
                body:
                  "You win the shipper, you keep the shipper. LaneRunner opens the door — the rest is your business.",
              },
              {
                icon: ShieldCheck,
                title: "No Margin Taken",
                body:
                  "We charge brokers for access, not margin on freight. Your bid is your bid. Your shipper is your shipper.",
              },
              {
                icon: Users,
                title: "Vetted Network",
                body:
                  "Every shipper and broker reviewed manually. Quality over volume.",
              },
              {
                icon: Building2,
                title: "Built by Freight People",
                body:
                  "Tim Freyer founded LaneRunner after 12 years as a broker. We know what works because we lived it.",
              },
              {
                icon: Network,
                title: "Private Networks",
                body:
                  "Shippers organize brokers by region, equipment, or relationship. Targeted freight, curated competition.",
              },
              {
                icon: Target,
                title: "Open Marketplace",
                body:
                  "For LR Direct Full brokers, access every open LR Direct load. Wider reach, same direct relationship model.",
              },
            ].map((c) => {
              const I = c.icon;
              return (
                <div key={c.title} className="rounded-2xl border border-gray-200 p-6 hover:shadow-lg hover:border-[#02857E]/30 transition bg-white">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#02857E]/10 text-[#02857E]">
                    <I className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 text-lg font-bold text-gray-900">{c.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 7. FAQ                                                         */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-24 bg-[#F7FBFB]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-gray-900">
              Common questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {[
              {
                q: "What is LR Direct?",
                a:
                  "A direct freight marketplace built into LaneRunner. Shippers post freight directly to private broker networks or the open marketplace. Brokers compete in fair, vetted environments. Every relationship is direct — no margin taken.",
              },
              {
                q: "How is this different from a load board?",
                a:
                  "Traditional load boards: 200 bids in 4 minutes, phantom freight, race to the bottom. LR Direct: curated broker pool per shipper, real freight with real urgency, direct relationships.",
              },
              {
                q: "What does it cost?",
                a:
                  "Shippers: free, always. Brokers: LR Direct + Load Board $99.99/mo (bid on shipper-invited private networks + full modern load board), or Full Platform $249.99/mo (everything above + open marketplace bidding).",
              },
              {
                q: "Why is Full Platform $249.99/mo?",
                a:
                  "You're getting open-marketplace LR Direct bidding on top of everything in LR Direct + Load Board — plus a dedicated account manager, 3 team seats, and 50 boost credits/mo. One won load can pay for an entire year of subscription. We don't take any margin on your freight.",
              },
              {
                q: "How do I get into a shipper's private network?",
                a:
                  "Two ways: 1) Shipper invites you via email or shares their LaneRunner ID with you. 2) You find their ID through your industry connections and request to join. Either way, you'll need an active broker subscription ($99.99/mo LR Direct + Load Board, or $249.99/mo Full Platform).",
              },
              {
                q: "What if I'm invited but don't have an account?",
                a:
                  "Click the invitation link in the email. You'll be routed to LR Direct + Load Board signup ($99.99/mo). After subscribing, you're automatically added to the shipper's private network.",
              },
              {
                q: "Can I bid on open marketplace LR Direct loads with LR Direct + Load Board?",
                a:
                  "No. That tier covers private-network bidding only. To bid on open marketplace LR Direct loads, upgrade to Full Platform ($249.99/mo).",
              },
              {
                q: "Do shippers pay anything?",
                a:
                  "No. LaneRunner is free for shippers, always. Shippers generate the freight that makes the marketplace work. We don't charge supply side.",
              },
              {
                q: "Can I cancel anytime?",
                a: "Yes. Cancel anytime, prorated. No long-term contracts.",
              },
              {
                q: "How long does shipper approval take?",
                a: "Manual review typically completes within 24 hours.",
              },
            ].map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-gray-200">
                <AccordionTrigger className="text-left text-base font-semibold text-gray-900 hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gray-600 leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 8. FINAL CTA                                                   */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-24 overflow-hidden bg-gradient-to-br from-[#02857E] via-[#026d67] to-[#014c47]">
        <div aria-hidden className="absolute -top-32 left-1/3 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-6 text-center text-white">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Ready to start?
          </h2>
          <p className="mt-4 text-lg text-white/80">
            Direct relationships. Real freight. No margin taken.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
            <div className="rounded-2xl bg-white/10 backdrop-blur-sm ring-1 ring-white/15 p-7 text-left">
              <p className="text-xs font-bold tracking-wider text-white/70">FOR SHIPPERS</p>
              <h3 className="mt-2 text-xl font-bold">Sign Up Free</h3>
              <p className="mt-2 text-sm text-white/80">
                Build your broker network and post freight directly.
              </p>
              <Button
                asChild
                className="mt-5 w-full rounded-full bg-white text-[#02857E] hover:bg-white/90 font-semibold"
              >
                <Link to="/auth?mode=signup&role=shipper">Sign Up Free <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="rounded-2xl bg-white/10 backdrop-blur-sm ring-1 ring-white/15 p-7 text-left">
              <p className="text-xs font-bold tracking-wider text-white/70">FOR BROKERS</p>
              <h3 className="mt-2 text-xl font-bold">View Broker Plans</h3>
              <p className="mt-2 text-sm text-white/80">
                Choose Premium or LR Direct Full.
              </p>
              <Button
                asChild
                variant="outline"
                className="mt-5 w-full rounded-full border-2 border-white bg-transparent text-white hover:bg-white/10 font-semibold"
              >
                <Link to="/pricing?role=broker">View Broker Plans <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default LRDirectAccessPage;
